/**
 * reminder-list.component.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Component, ElementRef, Input, OnInit, ViewChild } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import * as moment from "moment";

import { AlertService } from "../common/alert.service";
import { Reminder } from "./reminder";
import { WatchlistEntry } from "./watchlist-entry";
import { WatchlistService } from "./watchlist.service";

/**
 * The {@link ReminderListComponent} enumerates the {@link Reminder}s that a
 * user has set for themself, as well as providing options to create or remove
 * such reminders.
 */
@Component({
  selector: "reminder-list",
  template: `
    <h2>
      Reminders
      <span class="badge success">{{watchlist.activeReminderCount()}} active</span>
      <span class="badge">{{watchlist.reminders.length - watchlist.activeReminderCount()}} upcoming</span>
    </h2>
    <table class="table table-responsive">
      <thead *ngIf="showHeaders">
        <tr>
          <th>Timestamp</th>
          <th>Summary</th>
          <th><!-- Remove --></th>
        </tr>
      </thead>
      <tbody>
        <!-- add entry -->
        <tr>
          <td>
            <input
              type="datetime-local"
              class="form-control"
              [formControl]="triggerTime">
          </td>
          <td width="50%">
            <input
              type="text"
              class="form-control"
              [formControl]="summary"
              placeholder="Enter reminder memo...">
          </td>
          <td>
            <button
              class="btn btn-success form-control"
              (click)="add()"
              [disabled]="watchlist.reminderListObjectId === null">
              <span class="glyphicon glyphicon-plus"></span>
              Create Reminder
            </button>
          </td>
        </tr>
        <!-- existing entries -->
        <tr
          *ngFor="let reminder of (watchlist.reminders | paginate)"
          [class.bg-warning]="reminder.alert">
          <td class="text-center" style="padding-right: 60px">
            <span *ngIf="reminder.alert">
              <span class="glyphicon glyphicon-exclamation-sign">
              </span>
              &emsp;
            </span>
            {{reminder.triggerTime | date:"MMM d, y h:mm a"}}
          </td>
          <td style="padding-left: 22px">
            {{reminder.summary}}
          </td>
          <td>
            <button
              *ngIf="reminder.hasLink"
              type="button"
              class="btn btn-info form-control half"
              (click)="open(reminder)">
              <span class="glyphicon glyphicon-folder-open"></span>
              <!-- Open -->
            </button>
            <button
              type="button"
              class="btn btn-danger form-control"
              [class.half]="reminder.hasLink"
              (click)="remove(reminder)">
              <span class="glyphicon glyphicon-trash"></span>
              {{reminder.hasLink ? "" : " Remove"}}
            </button>
          </td>
        </tr>
      </tbody>
      <caption *ngIf="!watchlist.reminders?.length">
        No scheduled reminders!
      </caption>
    </table>
    <pager [asNeeded]="true" [array]="watchlist.reminders"></pager>
`,
  styles: [`
    caption {
      caption-side: bottom;
      text-align: center;
      font-size: 1.7em;
      font-style: italic;
      padding: 0.5em;
    }
    .faded {
      color: #DDDDDD;
    }
    table.table>tbody>tr>td {
      vertical-align: middle;
    }
    input[type=datetime-local] {
      text-align: center;
    }
    .btn.half {
      width: calc(50% - 2px);
    }
  `]
})
export class ReminderListComponent implements OnInit
{
  /**
   * Whether to display labels above the columns.
   */
  @Input()
  public showHeaders : boolean = true;

  /**
   * The text note associated with a new reminder.
   */
  public summary : FormControl = new FormControl("", Validators.required);

  /**
   * The time a new reminder is scheduled for.
   */
  public triggerTime : FormControl = new FormControl(null, Validators.required);

  /**
   * The form object holding the properties for the new reminder.
   */
  public form : FormGroup = new FormGroup({
    summary: this.summary,
    triggerTime: this.triggerTime
  });

  public constructor (public watchlist : WatchlistService) {}

  /**
   * Set up the forms on load.
   */
  public ngOnInit () : void
  {
    this.triggerTime.setValue(this.thisHourTomorrow);
    // this.triggerTime.valueChanges.subscribe(
    //   (v : any) => console.info(v, (v instanceof Date)));
  }

  /**
   * Helper to retrieve the hour-aligned date 24 hours from now, rounded down.
   */
  private get thisHourTomorrow () : Date
  {
    return moment().add(1, "d").startOf("hour").toDate();
  }

  /**
   * Create a new reminder.
   */
  public add () : void
  {
    if (this.triggerTime.invalid)
    {
      return alert("You must enter a date to create a reminder!");
    }
    if (this.summary.invalid)
    {
      return alert("You must enter a memo/label to create a reminder!");
    }

    this.watchlist.addReminder(
      this.summary.value, this.triggerTime.value
    ).subscribe((added : boolean) => {
      this.form.reset();
    });
  }

  /**
   * Remove a reminder from the list and cancel its notification.
   * @param reminder reminder to be removed
   */
  public remove (reminder : Reminder) : void
  {
    this.watchlist.removeReminder(reminder.notificationId).subscribe();
    // TODO: failure
  }

  public open (reminder : Reminder) : void
  {
    this.watchlist.openReminderLinkedRecord(reminder).subscribe();
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/watchlist/reminder-list.component.ts